import React, { useState, useRef, useEffect, useMemo } from 'react';
import { prizes as defaultPrizes, getRandomPrize } from '../data/prizes';
import { getPrizesByGameId } from '../services/firestoreService';

// Glossy raffle-drum segments: ember/gold/ink rotation, each paired with a
// legible text color. Rendered via radial gradients (defined once in <defs>)
// for the embossed, carnival-wheel look.
const SEGMENT_STYLES = [
  { grad: 'segEmber', text: '#FAF8F3' },
  { grad: 'segGold', text: '#17140F' },
  { grad: 'segEmber', text: '#FAF8F3' },
  { grad: 'segInk', text: '#FAF8F3' },
];

const RIVET_COUNT = 28;

const Wheel = ({
  onSpinComplete,
  isSpinning,
  handleSpin,
  disabled,
  gameId,
  guaranteed = false,
}) => {
  const [rotation, setRotation] = useState(0);
  const [prizes, setPrizes] = useState(defaultPrizes);
  const [probabilities, setProbabilities] = useState([]);
  const [wheelSegments, setWheelSegments] = useState([]);
  const [initialized, setInitialized] = useState(false);
  const [justLanded, setJustLanded] = useState(false);
  const spinSoundRef = useRef(null);

  useEffect(() => {
    async function loadCustomPrizes() {
      if (!gameId) {
        setPrizes(defaultPrizes);
        setInitialized(true);
        return;
      }
      try {
        const customPrizes = await getPrizesByGameId(gameId);
        if (customPrizes && customPrizes.length > 0) {
          const probs = customPrizes.map(p => p.probability / 100);
          setPrizes(customPrizes);
          setProbabilities(probs);
        } else {
          setPrizes(defaultPrizes);
        }
      } catch {
        setPrizes(defaultPrizes);
      } finally {
        setInitialized(true);
      }
    }
    loadCustomPrizes();
  }, [gameId]);

  useEffect(() => {
    if (!prizes.length) return;
    const SEGMENT_COUNT = 12;
    const segments = [];
    if (prizes.length >= SEGMENT_COUNT) {
      for (let i = 0; i < SEGMENT_COUNT; i++) segments.push(prizes[i % prizes.length]);
    } else {
      for (let i = 0; i < SEGMENT_COUNT; i++) {
        segments.push(prizes[Math.floor(Math.random() * prizes.length)]);
      }
    }
    setWheelSegments(segments);
  }, [prizes]);

  const selectRandomPrize = () => {
    if (!prizes.length) return null;
    const pool = guaranteed ? prizes.filter(p => !p.isNoReward) : prizes;
    const poolProbs = guaranteed
      ? probabilities.filter((_, i) => !prizes[i].isNoReward)
      : probabilities;
    const effectivePrizes = pool.length > 0 ? pool : prizes;
    const effectiveProbs = poolProbs.length === effectivePrizes.length ? poolProbs : [];

    if (effectiveProbs.length === effectivePrizes.length && effectiveProbs.length > 0) {
      const total = effectiveProbs.reduce((s, p) => s + p, 0);
      const norm = effectiveProbs.map(p => (total > 0 ? p / total : 1 / effectiveProbs.length));
      const rand = Math.random();
      let cum = 0;
      for (let i = 0; i < effectivePrizes.length; i++) {
        cum += norm[i];
        if (rand <= cum) return effectivePrizes[i];
      }
      return effectivePrizes[effectivePrizes.length - 1];
    }
    if (guaranteed && pool.length > 0) return pool[Math.floor(Math.random() * pool.length)];
    return getRandomPrize();
  };

  const spinWheel = () => {
    if (isSpinning || disabled || !prizes.length || !wheelSegments.length) return;

    handleSpin();
    if (spinSoundRef.current) {
      spinSoundRef.current.currentTime = 0;
      spinSoundRef.current.play().catch(() => {});
    }

    const selected = selectRandomPrize();
    const matching = wheelSegments
      .map((seg, i) => ({ seg, i }))
      .filter(item => item.seg.id === selected.id);

    if (!matching.length) return;

    const { i: segIndex } = matching[Math.floor(Math.random() * matching.length)];
    const segAngle = 360 / 12;
    // Center angle of this segment in the unrotated SVG (segments start at -90°)
    const midDeg = (segIndex + 0.5) * segAngle - 90;
    // Total rotation needed to bring this segment's center to the top pointer (270°)
    const targetAngle = ((270 - midDeg) % 360 + 360) % 360;
    // Subtract current wheel position so we land exactly at the pointer
    const currentMod = rotation % 360;
    const delta = ((targetAngle - currentMod) % 360 + 360) % 360 || 360;
    const baseSpins = 5 + Math.floor(Math.random() * 3);
    const spinRotation = baseSpins * 360 + delta;

    setRotation(prev => prev + spinRotation);

    setTimeout(() => {
      setJustLanded(true);
      setTimeout(() => setJustLanded(false), 750);
      if (onSpinComplete) onSpinComplete(selected);
    }, 5000);
  };

  // SVG wheel rendering
  const N = wheelSegments.length || 12;
  const CX = 150, CY = 150, R = 138;
  const toRad = d => d * Math.PI / 180;

  const segments = wheelSegments.map((seg, i) => {
    const a1 = toRad(i * 360 / N - 90);
    const a2 = toRad((i + 1) * 360 / N - 90);
    const x1 = CX + R * Math.cos(a1);
    const y1 = CY + R * Math.sin(a1);
    const x2 = CX + R * Math.cos(a2);
    const y2 = CY + R * Math.sin(a2);
    const largeArc = 360 / N > 180 ? 1 : 0;

    const midDeg = (i + 0.5) * 360 / N - 90;
    const tR = R * 0.66;
    const tx = CX + tR * Math.cos(toRad(midDeg));
    const ty = CY + tR * Math.sin(toRad(midDeg));
    // Flip text on the left half so it's never upside-down
    const textRotate = (midDeg > 90 && midDeg < 270) ? midDeg + 180 : midDeg;

    // Cap labels at two stacked lines (first word alone, the rest joined)
    // so multi-word prize names — and any trailing emoji a restaurant typed
    // into the name — never spill into neighboring slices.
    const rawWords = (seg.name || '').trim().split(/\s+/).filter(Boolean);
    const lines = rawWords.length > 1 ? [rawWords[0], rawWords.slice(1).join(' ')] : rawWords;

    const style = SEGMENT_STYLES[i % SEGMENT_STYLES.length];
    const fontSize = lines.length > 1 ? 10.5 : 13;
    const lineH = fontSize + 2;
    // Available chord width at this slice's text radius, with a safety margin
    const availWidth = 2 * tR * Math.sin(Math.PI / N) * 0.94;

    return { i, color: `url(#${style.grad})`, textColor: style.text, x1, y1, x2, y2, largeArc, tx, ty, textRotate, lines, fontSize, lineH, availWidth };
  });

  const accent = '#FF4D2E';

  // Static ring of rivets sitting on the gold rim, outside the rotating disc,
  // with a staggered glow animation to fake a chasing carnival-light effect.
  const rivets = useMemo(() => {
    return Array.from({ length: RIVET_COUNT }, (_, i) => {
      const deg = (i / RIVET_COUNT) * 360;
      const rad = toRad(deg - 90);
      const x = 50 + 48.5 * Math.cos(rad);
      const y = 50 + 48.5 * Math.sin(rad);
      return { i, x, y, delay: (i / RIVET_COUNT) * 1.6 };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="wheel-container relative w-full max-w-xs sm:max-w-sm md:max-w-md mx-auto select-none">
      <audio ref={spinSoundRef} preload="auto">
        <source src="/sounds/wheel-spin.mp3" type="audio/mpeg" />
      </audio>

      {/* Pointer at top */}
      <div style={{
        position: 'absolute', top: 0, left: '50%',
        transform: 'translateX(-50%) translateY(-32%)',
        zIndex: 20, pointerEvents: 'none',
        filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.45))',
      }}>
        <svg viewBox="0 0 36 44" width="32" height="40">
          <defs>
            <linearGradient id="pointerGrad" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor="#F5D68C" />
              <stop offset="55%" stopColor="#D9A441" />
              <stop offset="100%" stopColor="#9C6F1F" />
            </linearGradient>
          </defs>
          <path d="M18 42 L3 16 A15 15 0 1 1 33 16 Z" fill="url(#pointerGrad)" stroke="#17140F" strokeWidth="1.5"/>
          <circle cx="18" cy="16" r="5.5" fill="#17140F"/>
        </svg>
      </div>

      {/* Wheel */}
      <div style={{ position: 'relative', width: '100%', paddingTop: '100%', marginTop: 16 }}>
        {/* Glow */}
        <div style={{
          position: 'absolute', inset: -16, borderRadius: '50%',
          background: `radial-gradient(circle, ${accent}55, transparent 70%)`,
          filter: 'blur(18px)', zIndex: 0,
          transition: 'opacity 0.5s',
          opacity: isSpinning ? 1 : 0.65,
        }} />

        {/* Outer disc — gold metallic rim */}
        <div style={{
          position: 'absolute', inset: 0, borderRadius: '50%',
          background: 'linear-gradient(145deg, #F5D68C 0%, #D9A441 35%, #9C6F1F 75%, #6E4E15 100%)',
          padding: 10,
          boxShadow: '0 16px 38px rgba(0,0,0,0.45), 0 4px 12px rgba(0,0,0,0.25), inset 0 0 0 2px rgba(255,255,255,0.25)',
        }}>
          {/* Rotating SVG */}
          <div style={{
            width: '100%', height: '100%', borderRadius: '50%',
            transform: `rotate(${rotation}deg)`,
            transition: isSpinning ? 'transform 5s cubic-bezier(0.17, 0.67, 0.12, 1)' : 'none',
            boxShadow: 'inset 0 0 0 3px rgba(23,20,15,0.5)',
          }}>
            {!initialized ? (
              <div style={{ width: '100%', height: '100%', borderRadius: '50%', background: 'rgba(23,20,15,0.06)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <div style={{ width: 24, height: 24, border: '3px solid rgba(255,77,46,0.25)', borderTopColor: '#FF4D2E', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
              </div>
            ) : (
              <svg viewBox="0 0 300 300" style={{ display: 'block', width: '100%', height: '100%', borderRadius: '50%', overflow: 'hidden' }}>
                <defs>
                  <radialGradient id="segEmber" cx="35%" cy="30%" r="75%">
                    <stop offset="0%" stopColor="#FF8B67" />
                    <stop offset="55%" stopColor="#FF4D2E" />
                    <stop offset="100%" stopColor="#C43417" />
                  </radialGradient>
                  <radialGradient id="segGold" cx="35%" cy="30%" r="75%">
                    <stop offset="0%" stopColor="#F5D68C" />
                    <stop offset="55%" stopColor="#D9A441" />
                    <stop offset="100%" stopColor="#9C6F1F" />
                  </radialGradient>
                  <radialGradient id="segInk" cx="35%" cy="30%" r="75%">
                    <stop offset="0%" stopColor="#4A443C" />
                    <stop offset="55%" stopColor="#241F19" />
                    <stop offset="100%" stopColor="#0D0B08" />
                  </radialGradient>
                  <radialGradient id="hubGrad" cx="35%" cy="30%" r="75%">
                    <stop offset="0%" stopColor="#F9E2AA" />
                    <stop offset="50%" stopColor="#D9A441" />
                    <stop offset="100%" stopColor="#7A5518" />
                  </radialGradient>
                </defs>
                {segments.map(({ i, color, textColor, x1, y1, x2, y2, largeArc, tx, ty, textRotate, lines, fontSize, lineH, availWidth }) => (
                  <g key={i}>
                    <path
                      d={`M ${CX} ${CY} L ${x1} ${y1} A ${R} ${R} 0 ${largeArc} 1 ${x2} ${y2} Z`}
                      fill={color}
                      stroke="#F5D68C"
                      strokeWidth="2.5"
                    />
                    <g transform={`translate(${tx}, ${ty}) rotate(${textRotate})`}>
                      {lines.map((line, li) => {
                        const estWidth = line.length * fontSize * 0.58;
                        const compress = estWidth > availWidth;
                        return (
                          <text
                            key={li}
                            x="0"
                            y={li * lineH - ((lines.length - 1) * lineH) / 2}
                            textAnchor="middle"
                            dominantBaseline="middle"
                            fontSize={fontSize}
                            fontWeight="700"
                            fill={textColor}
                            fontFamily="Sora, system-ui"
                            {...(compress ? { textLength: availWidth, lengthAdjust: 'spacingAndGlyphs' } : {})}
                          >
                            {line}
                          </text>
                        );
                      })}
                    </g>
                  </g>
                ))}
                {/* Center hub */}
                <circle cx={CX} cy={CY} r="42" fill="url(#hubGrad)" stroke="#17140F" strokeWidth="2.5"/>
                {Array.from({ length: 8 }).map((_, gi) => {
                  const a = toRad(gi * 45);
                  return (
                    <circle key={`bezel-${gi}`} cx={CX + 34 * Math.cos(a)} cy={CY + 34 * Math.sin(a)} r="2.4" fill="#FAF3DE" opacity="0.85" />
                  );
                })}
              </svg>
            )}
          </div>

          {/* Slow rotating glass sheen — sits above the segments, doesn't affect spin */}
          <div className="wheel-sheen" style={{
            position: 'absolute', inset: 0, borderRadius: '50%',
            background: 'conic-gradient(from 0deg, transparent 0deg, rgba(255,255,255,0.28) 25deg, transparent 70deg, transparent 360deg)',
            pointerEvents: 'none', mixBlendMode: 'screen',
          }} />
        </div>

        {/* Static rivet ring on the rim */}
        <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
          {rivets.map(r => (
            <div key={r.i} className="wheel-rivet" style={{
              position: 'absolute', left: `${r.x}%`, top: `${r.y}%`,
              width: 6, height: 6, borderRadius: '50%',
              background: 'radial-gradient(circle at 35% 30%, #FFF6DC, #D9A441 60%, #7A5518)',
              transform: 'translate(-50%, -50%)',
              animationDelay: `${r.delay}s`,
            }} />
          ))}
        </div>

        {/* Landing flash burst */}
        {justLanded && (
          <div className="wheel-landing-flash" style={{
            position: 'absolute', top: '50%', left: '50%',
            width: '30%', height: '30%', borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(249,226,170,0.95), rgba(217,164,65,0.3) 60%, transparent 75%)',
            zIndex: 18, pointerEvents: 'none',
          }} />
        )}

        {/* SPIN button */}
        <button
          onClick={spinWheel}
          disabled={isSpinning || disabled || !initialized || !prizes.length}
          className={[
            !isSpinning && !disabled && initialized && prizes.length ? 'wheel-hub-idle' : '',
            justLanded ? 'wheel-hub-bounce' : '',
          ].filter(Boolean).join(' ')}
          style={{
            position: 'absolute', top: '50%', left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '16%', height: '16%', borderRadius: '50%',
            zIndex: 20, display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontFamily: 'Sora, system-ui', fontWeight: 800,
            fontSize: 'clamp(9px, 2vw, 14px)', letterSpacing: '0.06em',
            cursor: (isSpinning || disabled || !initialized || !prizes.length) ? 'not-allowed' : 'pointer',
            transition: 'transform 0.12s',
            background: (isSpinning || disabled)
              ? 'radial-gradient(circle at 35% 30%, #E8CB88, #B78A2E 65%, #7A5518)'
              : 'radial-gradient(circle at 35% 30%, #FCEBB4, #D9A441 55%, #8C651F 100%)',
            color: '#3A2A0A',
            border: '3px solid #17140F',
            boxShadow: isSpinning
              ? `0 0 0 3px rgba(217,164,65,0.4), 0 6px 16px rgba(0,0,0,0.4)`
              : `0 0 0 3px rgba(217,164,65,0.5), 0 10px 22px rgba(0,0,0,0.4), inset 0 2px 0 rgba(255,255,255,0.55)`,
          }}
        >
          {!initialized
            ? <div style={{ width: 14, height: 14, border: '2px solid rgba(23,20,15,0.3)', borderTopColor: '#17140F', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
            : disabled ? '✕'
            : !prizes.length ? '–'
            : <span style={{ opacity: isSpinning ? 0.6 : 1 }}>SPIN</span>
          }
        </button>
      </div>
    </div>
  );
};

export default Wheel;
